import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { leadership } from '../../data/leadership';
import './About.css';

function About() {
  const awardGallery = [
    {
      src: '/images/about/excellence_award/image001.jpg',
      alt: 'Harris County Historical Society Award of Excellence ceremony'
    },
    {
      src: '/images/about/excellence_award/44a28964-1.png',
      alt: 'Award of Excellence recipient'
    },
    {
      src: '/images/about/excellence_award/ab1a6861-1.png',
      alt: 'Award of Excellence presentation'
    }
  ];
  const [activeAwardIndex, setActiveAwardIndex] = useState(0);

  const showPreviousAward = () => {
    setActiveAwardIndex((prev) => (prev === 0 ? awardGallery.length - 1 : prev - 1));
  };

  const showNextAward = () => {
    setActiveAwardIndex((prev) => (prev === awardGallery.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="about">
      <section className="page-hero">
        <h1>About Us</h1>
        <p>Preserving and sharing Harris County's heritage since 1923</p>
      </section>

      <div className="page-container">
        <section className="section">
          <h2>Our Purpose</h2>
          <div className="purpose-content">
            <p>
              "The purpose of the Harris County Historical Society is to encourage, advocate 
              and administer the discovery, collection, preservation and publication of historical 
              information, records, oral histories, visuals and other materials relating to the 
              history of Harris County, and to educate the public about the important history of 
              Harris County, Texas."
            </p>
          </div>
        </section>

        <section className="section">
          <h2>Our History</h2>
          <div className="history-content">
            <p>The Harris County Historical Society was founded on October 2, 1923, in honor of the 88th anniversary of the Battle of Gonzales. Early members were sons, daughters, and grandchildren of signers of the Texas Declaration of Independence or veterans of San Jacinto who had detailed knowledge of historical events. The Society's founding members were Andrew Jackson Houston, Adele Lubbock Briscoe Looscan, Clarence R. Wharton, Sam Houston Dixon, Mrs. I.B. McFarland, and Judge Lewis R. Bryan.</p>
            
            <div className="history-quote">
              <p>"Organization of the Harris County Historical Society will take place at 3 P.M. today in the rooms of the University Club, it was announced by Colonel A. J. Houston of La Porte. A special invitation is extended to school teachers interested in Texas history. The meeting was called at the request of the State Historical Association, officials of which have asked that such organizations be formed in the many counties of Texas."</p>
              <cite>— Houston Post, Saturday, September 22, 1923</cite>
            </div>
            <div className="history-images">
              <img 
                src="/images/about/1.jpg" 
                alt="Harris County Historical Society building"
                loading="lazy"
                width="600"
                height="400"
              />
              <img 
                src="/images/about/2.jpg" 
                alt="Historical documents and artifacts"
                loading="lazy"
                width="600"
                height="400"
              />
            </div>
          </div>
        </section>

        <section className="section award-section">
          <h2>Award of Excellence</h2>
          <div className="award-intro">
            <p>
              The Harris County Historical Society presents an annual <strong>Award for Excellence</strong> to
              an undergraduate or graduate student for a public history project focused on Houston or Harris County history.
              The scholarship is awarded in partnership with the{' '}
              <a href="https://www.uh.edu/class/ctr-public-history/" target="_blank" rel="noopener noreferrer">University of Houston Center for Public History</a>.
            </p>
            <p>
              Projects are completed between June and May of the award year. The recipient presents their
              project or a summary at the Harris County Historical Society dinner. The projects are completed by undergraduate juniors and seniors
              or graduate students at UH who demonstrate excellence in academics and an interest in the public history
              of Houston or Harris County.
            </p>
            <p className="award-deadline">
              <strong>Submission deadline: May 15, 2026.</strong>{' '}
              <a href="https://www.uh.edu/class/ctr-public-history/news/student-opportunities/harris-county-scholarship.php" target="_blank" rel="noopener noreferrer">
                Apply through the UH Center for Public History
              </a>.
            </p>
          </div>

          <div className="award-carousel" aria-label="Award of Excellence photo carousel">
            <figure className="award-photo-card">
              <img
                src={awardGallery[activeAwardIndex].src}
                alt={awardGallery[activeAwardIndex].alt}
                loading="lazy"
              />
            </figure>

            <div className="award-carousel-controls">
              <button
                type="button"
                className="award-carousel-button"
                onClick={showPreviousAward}
                aria-label="Previous award photo"
              >
                Previous
              </button>
              <button
                type="button"
                className="award-carousel-button"
                onClick={showNextAward}
                aria-label="Next award photo"
              >
                Next
              </button>
            </div>

            <div className="award-carousel-dots" role="tablist" aria-label="Choose award photo">
              {awardGallery.map((photo, index) => (
                <button
                  key={photo.src}
                  type="button"
                  role="tab"
                  aria-selected={activeAwardIndex === index}
                  aria-label={`View award photo ${index + 1}`}
                  className={`award-carousel-dot ${activeAwardIndex === index ? 'active' : ''}`}
                  onClick={() => setActiveAwardIndex(index)}
                />
              ))}
            </div>
          </div>
        </section>

        <div className="navigation-section">
          <Link to="/about/handout" className="navigation-row">
            <div className="row-content">
              <h3>Digital Handout</h3>
              <p>Access our comprehensive digital handout with detailed information about the Harris County Historical Society.</p>
            </div>
            <span className="button">View Handout</span>
            <div className="hover-background" style={{ backgroundImage: `url(/images/about/2.jpg)` }} />
          </Link>
        </div>

        <section className="leadership-section section">
          <h2>Leadership</h2>
          <div className="leadership-grid">
            {leadership.map((leader) => (
              <div className="leader-card" key={leader.role}>
                <div className="card-image">
                  <img src={leader.image} alt={leader.name} loading="lazy" width="300" height="300" />
                </div>
                <h3>{leader.role}</h3>
                <p>{leader.name}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="committees-section section">
          <h2>Committees</h2>
          <div className="committees-grid">
            <div className="committee-card">
              <div className="card-image" style={{ border: 'none' }}>
              </div>
              <h3>Membership</h3>
              <p>&nbsp;</p>
            </div>
            <div className="committee-card">
              <div className="card-image">
                <img src="/images/people/Amy Robinson.jpeg" alt="Amy Ahlbrand Robinson" loading="lazy" width="300" height="300" />
              </div>
              <h3>Media</h3>
              <p>Amy Ahlbrand Robinson</p>
            </div>
          </div>
        </section>

        <section className="programs-section section">
          <h2>Our Programs</h2>
          <div className="programs-content">
            <h3>Dinner Programs</h3>
            <p>We host dinner programs September through December, and February through May on the second Wednesday evening of these months from 6 to 8:30 PM. We take a break in January, June, July and August.</p>
            <div className="button-group">
              <Link to="/events" className="button">View Our Programs</Link>
            </div>
          </div>
        </section>

        <div className="navigation-section">
          <Link to="/media" className="navigation-row">
            <div className="row-content">
              <h3>Our Media</h3>
              <p>Discover our archive of markers, digital publications, and video content.</p>
            </div>
            <span className="button">View Collection</span>
            <div className="hover-background" style={{ backgroundImage: `url(/images/home/media.png)` }} />
          </Link>
        </div>

        <section className="membership-section section">
          <h2>Membership Benefits</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <h3>Knowledge Enrichment</h3>
              <p>Gain a deeper understanding of the region's history, culture, and significant events.</p>
            </div>
            <div className="benefit-card">
              <h3>Community Connection</h3>
              <p>Engage with others who share your interest in local history.</p>
            </div>
            <div className="benefit-card">
              <h3>Access to Resources</h3>
              <p>Get exclusive access to materials, archives, and expert speakers.</p>
            </div>
            <div className="benefit-card">
              <h3>Volunteer Opportunities</h3>
              <p>Get involved through various volunteer and leadership positions.</p>
            </div>
          </div>
          <div className="membership-cta">
            <Link to="/support" className="button">Become a Member</Link>
          </div>
        </section>
      </div>
    </div>
  );
}

export default About;